import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import HousePentagonIcon from '@/assets/icons/HousePentagonIcon';
import {
  sGetActiveProperty,
  sGetDocumentsToSigned,
} from '@/store/reducers/properties';
import { connect } from 'react-redux';
import { getPropertyAddress } from '@/utils/properties';
import documentsToSignImage from '@/assets/images/documentsToSignImage.png';
import CalendarLineIcon from '@/assets/icons/CalendarLineIcon';
// import { Clock3 } from 'lucide-react';
import StickyNoteIcon from '@/assets/icons/StickyNoteIcon';
import { Skeleton } from '@/components/ui/skeleton';
import WithHeader from '@/components/layouts/WithHeader';
import {
  fetchDocumentsToSignedStatus,
  fetchSurveyorDocumentSignUrl,
} from '@/api/documents';
import useDashboardLinkPrefix from '@/hooks/useDashboardLinkPrefix';
import { acSetDocumentsToSigned } from '@/store/actions/properties';

const DocumentsState = ({
  activeProperty,
  documentsToSigned,
  setDocumentsToSigned,
}) => {
  const history = useHistory();
  const [signDocumentUrl, setSignDocumentUrl] = useState(undefined);
  const [firstDocAvailableToSign, setFirstDocAvailableToSign] =
    useState(undefined);
  const dashboardLinkPrefix = useDashboardLinkPrefix();

  const createdDate = new Date(firstDocAvailableToSign?.createdDate);
  // const hours = String(createdDate.getHours()).padStart(2, '0');
  // const minutes = String(createdDate.getMinutes()).padStart(2, '0');
  // const createdTimeText = `${hours}:${minutes}`;
  const createdDateOnly = new Date(createdDate);
  const createdDateText = createdDateOnly.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  useEffect(() => {
    if (firstDocAvailableToSign) {
      const fetchData = async () => {
        const [success, response] = await fetchSurveyorDocumentSignUrl(
          firstDocAvailableToSign.revisionId
        );
        if (success) {
          setSignDocumentUrl(response);
        }
      };
      fetchData();
    }
  }, [firstDocAvailableToSign]);

  useEffect(() => {
    if (documentsToSigned?.length > 0) {
      setFirstDocAvailableToSign(
        documentsToSigned?.find(
          (el) => el.boligmappaNumber === activeProperty?.boligmappaNumber
        )
      );
    }
  }, [documentsToSigned, activeProperty]);

  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      const [error, data] = await fetchDocumentsToSignedStatus(
        firstDocAvailableToSign?.id
      );
      if (error) {
        stopped = true;
        return;
      }
      const { documentStatus } = data || {};
      if (documentStatus === 'Signed') {
        stopped = true;
        setDocumentsToSigned([]);
        history.push(`${dashboardLinkPrefix}/home`);
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [firstDocAvailableToSign]);

  return (
    <WithHeader
      contentClass="!flex flex-col items-center !px-2 !sm:px-6"
      minFullHeight
      maxWidth="xl"
    >
      <Card className="flex flex-col gap-3 w-full max-w-[600px] justify-self-center">
        {firstDocAvailableToSign ? (
          <>
            <img
              className="self-center max-w-[132px]"
              alt="document signing"
              src={documentsToSignImage}
            />
            <div className="flex flex-col gap-3 p-4 sm:p-8">
              <h2 className="font-bold text-[24px] leading-[150%] tracking-[0px]">
                Tilstandsrapport til signering
              </h2>
              <div className="flex flex-wrap items-center gap-[8px] sm:gap-[14px]">
                <div className="flex items-center gap-2">
                  <HousePentagonIcon />
                  <p className="text-sidebar-foreground/70 text-base tracking-[-0.04em] font-circular">
                    {getPropertyAddress(activeProperty)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarLineIcon />
                  <p className="text-sidebar-foreground/70 text-base tracking-[-0.04em] font-circular">
                    {createdDateText}
                  </p>
                </div>
                {/* <div className="flex items-center gap-2"> */}
                {/*   <Clock3 width={15} stroke="#808080" /> */}
                {/*   <p className="text-sidebar-foreground/70 text-base tracking-[-0.04em] font-circular"> */}
                {/*     {createdTimeText} */}
                {/*   </p> */}
                {/* </div> */}
                <div className="flex items-center gap-2">
                  <StickyNoteIcon />
                  <p className="text-sidebar-foreground/70 text-base tracking-[-0.04em] font-circular">
                    Versjon {firstDocAvailableToSign?.version}
                  </p>
                </div>
              </div>
              <h3 className="font-medium text-base leading-6 tracking-normal">
                Tilstandsrapport fra {firstDocAvailableToSign?.companyName} til
                signering for {getPropertyAddress(activeProperty)} med BankID er
                nå klar.
              </h3>
              <p className="font text-[15px] leading-[142%] tracking-normal">
                Tilstandsrapporten er et viktig dokument når bolig skifter eier.
                Som selger må du informere om alle forhold ved boligen som du
                vet om eller burde vite om, slik at kjøperen får et riktig bilde
                at boligens tilstand (jfr avhendigslova § 3-7).
                <br />
                <br />
                Les dokumentet nøye og kontakt takstforetaket dersom noe må
                rettes eller legges til, informasjon mangler eller om du har
                spørsmål.
                <br />
                <br />
                Ved å signere bekrefter du at du har lest rapporten og at
                opplysningene er gitt etter beste evne. En signert kopi sendes
                automatisk til takstselskapet
              </p>
            </div>
            {signDocumentUrl ? (
              <iframe
                title="Sign document"
                className="flex flex-col h-[935px] mt-4"
                src={signDocumentUrl}
              />
            ) : (
              <div className="flex flex-col items-center space-y-3 min-h-[600px] gap-3">
                <div className="flex flex-col space-y-2 gap-3 w-full max-w-[95%]">
                  <Skeleton className="bg-sidebar-accent h-4 w-full" />
                  <Skeleton className="bg-sidebar-border h-[200px] w-full self-center" />
                  <Skeleton className="bg-sidebar-border h-[400px] w-full self-center" />
                  <Skeleton className="bg-sidebar-accent h-4 w-full" />
                  <Skeleton className="bg-sidebar-accent h-4 w-full" />
                  <Skeleton className="bg-sidebar-accent h-4 w-full" />
                  <Skeleton className="bg-sidebar-accent h-4 w-full" />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col space-y-3 min-h-[600px] gap-3">
            <Skeleton className="bg-sidebar-accent h-[125px] w-[120px] self-center rounded-xl" />
            <div className="flex flex-col space-y-2 gap-3">
              <Skeleton className="bg-sidebar-accent h-4 w-full" />
              <Skeleton className="bg-sidebar-accent h-4 w-full" />
              <Skeleton className="bg-sidebar-accent h-4 w-full" />
              <Skeleton className="bg-sidebar-accent h-4 w-full" />
              <Skeleton className="bg-sidebar-accent h-4 w-full" />
            </div>
          </div>
        )}
      </Card>
    </WithHeader>
  );
};

const mapStateToProps = (state) => ({
  activeProperty: sGetActiveProperty(state),
  documentsToSigned: sGetDocumentsToSigned(state),
});

const mapDispatchToProps = (dispatch) => ({
  setDocumentsToSigned: (data) => dispatch(acSetDocumentsToSigned(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DocumentsState);
